import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Button, Typography } from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import { FormProvider, useForm } from 'react-hook-form';
import { has, pick } from 'lodash';

import { buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import AdminPage from '../AdminPage';
import { AdminPages } from '../AdminPages';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpTextField from '../../form/NgpTextField';
import {
    DEFAULT_GROUP_UUID,
    GROUPS_ADMIN_ALLOW_DRILL_IN_FIELD,
    GROUPS_ADMIN_BUSINESS_SERVICES_FIELD,
    GROUPS_ADMIN_DESCRIPTION_FIELD,
    GROUPS_ADMIN_LANDING_PAGE_FIELD,
    GROUPS_ADMIN_NAME_FIELD,
    GROUPS_ADMIN_PAGES_FIELD,
    GROUPS_ADMIN_SITES_FIELD,
    GROUPS_ADMIN_USERS_FIELD,
    GROUPS_ADMIN_DASHBOARDS_FIELD,
    NO_LANDING_PAGE_DEFAULT,
} from './GroupsAdminConstants';
import NgpTextarea from '../../form/NgpTextarea';
import { useFindById } from '../../../services/FormQueryService';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { Actions } from '../../page-framework';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { WARNING, createAlertConfig } from '../../../stores/AlertsStore';
import { useErrorWrapper, useGenericError } from '../../../services/FormErrorService';
import { GroupsAdminPagesSection } from './GroupsAdminPagesSection';
import { GroupsAdminUsersSection } from './GroupsAdminUsersSection';
import { GroupsAdminSitesSection } from './GroupsAdminSitesSection';
import { GroupsAdminBusinessServicesSection } from './GroupsAdminBusinessServicesSection';
import useSession from '../../../stores/SessionStore';
import NgpToggle from '../../form/NgpToggle';
import useEnvironment from '../../../stores/EnvironmentStore';
import { getLandingPageFromForm, useGroupAdminStyles } from './GroupsAdminUtils';
import { GroupsAdminLandingPageSelect } from './GroupsAdminLandingPageSelect';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import { usePlmMainMenuPages } from '../../side-nav/menus/PlmMainMenuPages';
import { GroupsAdminDashboardsSection } from './GroupsAdminDashboardsSection';
import { useDataService } from '../../../services/ApiDataService';

const functionName = AdminPages.Groups;
const newBreadcrumbText = t`New Group`;
const defaultFormValues = {
    [GROUPS_ADMIN_LANDING_PAGE_FIELD]: NO_LANDING_PAGE_DEFAULT.value,
};
const apiUrl = '/ipm/v1/admin/groups';
const goToUrl = '../';

export default function GroupsAdminDetails() {
    const styles = useGroupAdminStyles();
    const [dashboards, setDashboards] = useState([]);
    const [removeDefaultGroupDialogOpen, setRemoveDefaultGroupDialogOpen] = useState(false);
    const [pagesSelectionIsDirty, setPagesSelectionIsDirty] = useState(false);
    const [usersSelectionIsDirty, setUsersSelectionIsDirty] = useState(false);
    const [sitesSelectionDirty, setSitesSelectionDirty] = useState(false);
    const [dashboardsSelectionIsDirty, setDashboardsSelectionIsDirty] = useState(false);
    const [businessServicesSelectionDirty, setBusinessServicesSelectionDirty] = useState(false);
    const [, setShowDeleteButton] = useState(false);
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, handleSubmit, setError, watch } = methods;
    const recordId = useEntityIdParam();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const formDeleteService = useFormDeleteService();
    const formSaveService = useFormSaveService();
    const { errorWrapper } = useErrorWrapper();
    const handleGenericError = useGenericError();
    const [, sessionActions] = useSession();
    const [environment] = useEnvironment();
    const formUsers = watch(GROUPS_ADMIN_USERS_FIELD);
    const usersRef = useRef([]);
    const plmMainMenuPages = usePlmMainMenuPages();
    const dataService = useDataService();

    const isEditingDefaultGroup = recordId && recordId === DEFAULT_GROUP_UUID;

    useEffect(() => {
        (async () => {
            const { dashboards: dashboardData } = await dataService.get('/ipm/v1/dashboards');
            setDashboards(dashboardData.filter((data) => !data?.private));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submittedUsersHaveDefaultGroup = useCallback(() => {
        // Don't run this check if we're currently editing the default group
        if (recordId === DEFAULT_GROUP_UUID) return false;

        const submittedUsers = usersRef.current.filter((user) => formUsers.includes(user._id));
        const groupsOfSubmittedUsers = new Set();
        submittedUsers.forEach((submittedUser) => {
            // No need to track the system default users groups here as that user cannot be removed from the
            // default group
            if (submittedUser.source !== 'default') {
                submittedUser.groups.forEach((userGroup) => groupsOfSubmittedUsers.add(userGroup));
            }
        });

        return groupsOfSubmittedUsers.has(DEFAULT_GROUP_UUID);
    }, [formUsers, recordId]);

    const findByIdErrorHandler = useCallback(
        (errorObject) => {
            if (errorObject.getStatus && errorObject.getStatus() === 403) {
                return createAlertConfig(WARNING, t`You do not have permissions to view group details.`);
            }

            handleGenericError(errorObject);
        },
        [handleGenericError]
    );

    const { initialized, finding, formData } = useFindById({
        url: '/ipm/v1/admin/groups/',
        recordId,
        postFindByIdHandler: (group) => {
            setShowDeleteButton(true);

            return group;
        },
        resetFunc: reset,
        defaultValues: {
            ...defaultFormValues,
            [GROUPS_ADMIN_PAGES_FIELD]: Object.values(plmMainMenuPages).map((menuItem) => menuItem.path),
        },
        errorHandler: errorWrapper(findByIdErrorHandler, goToUrl),
    });

    const functionBreadCrumbs = [{ link: goToUrl, text: t`Groups` }];
    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, 'groupName', newBreadcrumbText);

    const dirtyCheck = (isDirty) =>
        isDirty ||
        pagesSelectionIsDirty ||
        usersSelectionIsDirty ||
        sitesSelectionDirty ||
        businessServicesSelectionDirty ||
        dashboardsSelectionIsDirty;

    const handleFormSubmit = (submitData) => {
        const dataToSave = isEditingDefaultGroup
            ? pick(
                  {
                      ...formData,
                      ...submitData,
                      userDashboards: has(submitData[GROUPS_ADMIN_DASHBOARDS_FIELD], 'ids')
                          ? submitData[GROUPS_ADMIN_DASHBOARDS_FIELD]
                          : { ids: submitData[GROUPS_ADMIN_DASHBOARDS_FIELD] },
                  },
                  ['users', 'userDashboards']
              )
            : {
                  ...formData,
                  ...submitData,
                  landingPage: getLandingPageFromForm(formData, submitData),
                  sites: has(submitData[GROUPS_ADMIN_SITES_FIELD], 'ids')
                      ? submitData[GROUPS_ADMIN_SITES_FIELD]
                      : { ids: submitData[GROUPS_ADMIN_SITES_FIELD] },
                  businessServices: has(submitData[GROUPS_ADMIN_BUSINESS_SERVICES_FIELD], 'ids')
                      ? submitData[GROUPS_ADMIN_BUSINESS_SERVICES_FIELD]
                      : { ids: submitData[GROUPS_ADMIN_BUSINESS_SERVICES_FIELD] },
                  userDashboards: has(submitData[GROUPS_ADMIN_DASHBOARDS_FIELD], 'ids')
                      ? submitData[GROUPS_ADMIN_DASHBOARDS_FIELD]
                      : { ids: submitData[GROUPS_ADMIN_DASHBOARDS_FIELD] },
              };

        return formSaveService
            .save({
                url: apiUrl,
                recordId: formData._id,
                dataToSave,
                setError,
            })
            .then((response) => {
                sessionActions.refresh(environment, false);
                return response;
            });
    };
    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);

    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteById(apiUrl, recordId),
            onDeleteGoTo: goToUrl,
        })();
    };

    return (
        <AdminPage
            pageId={buildPageId(functionName)}
            breadcrumbs={breadcrumbs}
            actions={
                <Actions>
                    {!isEditingDefaultGroup && (
                        <ConfirmDeleteButton handleConfirmedDelete={handleConfirmedDelete} plural={false} />
                    )}
                    <Button
                        onClick={() =>
                            submittedUsersHaveDefaultGroup()
                                ? setRemoveDefaultGroupDialogOpen(true)
                                : handleSubmit(handleSubmitFunction)()
                        }
                        variant="contained"
                        color="primary"
                    >
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <>
                <NgpTwoButtonDialog
                    title={t`Remove users from Default group?`}
                    confirmButtonText={t`Yes`}
                    cancelButtonText={t`No`}
                    open={removeDefaultGroupDialogOpen}
                    setOpen={setRemoveDefaultGroupDialogOpen}
                    disableBackdropClick
                    onConfirm={handleSubmit((data) =>
                        handleSubmitFunction({ ...data, removeDefaultGroupFromUsers: true })
                    )}
                    onCloseFunc={handleSubmit(handleSubmitFunction)}
                >
                    <div className={styles.removeDefaultGroupDialogContainer}>
                        <Typography variant="body1">
                            <Trans>
                                One or more of the users being added to this group are currently in the{' '}
                                <strong>Default</strong> group which allows full access to the nGeniusPULSE application
                                and will prevent this groups permissions from having any effect on those users. Would
                                you like to remove those users from the <strong>Default</strong> group?
                            </Trans>
                        </Typography>
                    </div>
                </NgpTwoButtonDialog>
                <FormProvider {...methods}>
                    <NgpForm
                        submitFunction={handleSubmitFunction}
                        initialized={initialized}
                        loading={finding || saving || deleting}
                        isDirtyFunc={dirtyCheck}
                        showSave={false}
                        focusFirstField={false}
                    >
                        {isEditingDefaultGroup && (
                            <div className={styles.detailsInfoWrapper}>
                                <InfoOutlined className={styles.detailsInfoIcon} />
                                <Trans>
                                    Only the users assigned to the <strong>Default</strong> group can be updated or
                                    deleted. The <strong>Default</strong> group&apos;s permissions cannot be changed.
                                </Trans>
                            </div>
                        )}
                        <FormSection title={t`Group Detail`}>
                            <NgpTextField
                                fieldName={GROUPS_ADMIN_NAME_FIELD}
                                label={t`Group Name`}
                                required
                                inputProps={{ size: 40, maxLength: 128 }}
                                disabled={isEditingDefaultGroup}
                            />
                            <NgpTextarea
                                fieldName={GROUPS_ADMIN_DESCRIPTION_FIELD}
                                label={t`Description`}
                                cols={50}
                                inputProps={{ maxLength: 255 }}
                                disabled={isEditingDefaultGroup}
                            />
                            <GroupsAdminLandingPageSelect
                                formData={formData}
                                disabled={isEditingDefaultGroup}
                                dashboards={dashboards}
                            />
                            <div className={styles.drillInToggle}>
                                <NgpToggle
                                    fieldName={GROUPS_ADMIN_ALLOW_DRILL_IN_FIELD}
                                    label={t`Allow Drill In`}
                                    helpTipText={
                                        <Trans>
                                            Controls group member ability to view details pages regarding entities found
                                            on list and overview pages.
                                        </Trans>
                                    }
                                    disabled={isEditingDefaultGroup}
                                />
                            </div>
                        </FormSection>
                        <GroupsAdminUsersSection
                            formData={formData}
                            usersRef={usersRef}
                            setUsersSelectionIsDirty={setUsersSelectionIsDirty}
                            isEditingDefaultGroup={isEditingDefaultGroup}
                        />
                        <GroupsAdminSitesSection
                            formData={formData}
                            setSitesSelectionDirty={setSitesSelectionDirty}
                            isEditingDefaultGroup={isEditingDefaultGroup}
                        />
                        <GroupsAdminBusinessServicesSection
                            formData={formData}
                            setBusinessServicesSelectionDirty={setBusinessServicesSelectionDirty}
                            isEditingDefaultGroup={isEditingDefaultGroup}
                        />
                        <GroupsAdminPagesSection
                            formData={formData}
                            setPagesSelectionIsDirty={setPagesSelectionIsDirty}
                            isEditingDefaultGroup={isEditingDefaultGroup}
                        />
                        <GroupsAdminDashboardsSection
                            formData={formData}
                            setDashboardsSelectionIsDirty={setDashboardsSelectionIsDirty}
                            dashboards={dashboards}
                        />
                    </NgpForm>
                </FormProvider>
            </>
        </AdminPage>
    );
}
